import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AddressModal from "../components/models/AddressModal";
import { generatePickupDates } from "../utils/helper";
import {
  AddAddress,
  CreateOrder,
  DeleteAddress,
  GetAddress,
} from "../apis/Api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const CheckOutPage = () => {
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedAddressData, setSelectedAddressData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showPickupDates, setShowPickupDates] = useState(false);
  const [cart, setCart] = useState([]);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [error, setError] = useState(null);
  const [isCartLoaded, setIsCartLoaded] = useState();
  const [totalPrice, setTotalPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const navigate = useNavigate();
  const getAddress = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("no user found");
      }

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timed out")), 10000)
      );

      const responsePromise = GetAddress({ cust_token: token });
      const response = await Promise.race([responsePromise, timeoutPromise]);
      if (response?.status === 200) {
        const addres = response.address ? JSON.parse(response.address) : [];

        setAddresses(addres);
      } else {
        throw new Error("no address found");
      }
    } catch (error) {
      if (error.status === 404) {
        setError("No address found.");

        return;
      }

      console.log("Error feching address:", error);
      setError(error.message || "Failed to load address. Please try again.");
      setAddresses([]);
      toast.error(`${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const removeAddress = async (id) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("no user found");
      }

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timed out")), 10000)
      );

      const responsePromise = DeleteAddress({ id, cust_token: token });
      const response = await Promise.race([responsePromise, timeoutPromise]);
      if (response?.status === 200) {
        toast.success("Address removed successfully");

        setUpdateTrigger((prev) => prev + 1);
      } else {
        throw new Error("Invalid response data");
      }
    } catch (error) {
      if (error.status === 404) {
        setError("No address removed.");
        return;
      }

      setError(error.message || "Failed to remove address. Please try again.");
      setAddresses([]);
      toast.error(`${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        const storedCart = localStorage.getItem("Cart");
        if (storedCart) {
          const cleanedCart = storedCart.replace(/\\/g, "");
          const parsedCart = JSON.parse(cleanedCart);

          setCart(parsedCart);
        }
      } catch (error) {
        console.log("Error initializing data:", error);
      } finally {
        setIsCartLoaded(true);
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    getAddress();
  }, [updateTrigger]);

  const calculateTotalPrice = () => {
    return cart.reduce((accumulator, item) => {
      return accumulator + parseFloat(item.payable_price) * item.quantity;
    }, 0);
  };

  useEffect(() => {
    const totalPrice = calculateTotalPrice();
    setTotalPrice(parseInt(totalPrice));
  }, [cart]);

  const handleSelectAddress = (index) => {
    setSelectedAddress(index);
    setSelectedAddressData(addresses[index]);

    setShowPickupDates(true);
  };

  const handleContinue = () => {
    if (selectedAddress == null || selectedDate == null) {
      toast.error("please select address & date slot");
      return;
    }
    PostCart();
  };

  const handleDateSelect = (date) => {
    const selected = generatePickupDates().find((d) => d.date === date);
    if (selected) {
      const formattedDate = `${selected.year}-${selected.monthIndex
        .toString()
        .padStart(2, "0")}-${date.toString().padStart(2, "0")}`;
      setSelectedDate(formattedDate);
    }
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const PostCart = async () => {
    setIsButtonDisabled(true);
    const cartData = localStorage.getItem("Cart");
    const token = localStorage.getItem("token");
    if (cartData && token) {
      const cleanedCart = cartData.replace(/\\/g, "");
      const parsedCart = JSON.parse(cleanedCart);

      try {
        const response = await CreateOrder({
          cust_token: token,
          Cart: parsedCart,
          total_price: `${totalPrice}`,
          address: selectedAddressData,
          preferred_date: `${selectedDate}`,
        });
        if (response && response?.status === 200) {
          toast.success("order successfully created");
          setTimeout(() => {
            navigate(
              `/cart-summary/${cart[0].brand_token}/${cart[0].model_token}`
            );
            localStorage.removeItem("Cart");
          }, 1500);
        } else {
          setIsButtonDisabled(false);
        }
      } catch (error) {
        console.log("Error posting cart:", error);
        toast.error(`${error.message}`);
      }
    } else {
      toast.error("please create a new cart");
    }
  };

  return (
    <>
      <Header />
      <div id="content" className="no-top no-bottom">
        <div
          className="container"
          style={{ paddingTop: "160px", marginBottom: "50px" }}
        >
          <h5>
            <strong>You are almost done!</strong>
          </h5>
          <div className="row">
            <div className="col-md-8">
              {/* Address Card */}
              <div className="card mb-3">
                <div className="card-body">
                  <h6>
                    <span className="badge bg-primary me-2">1</span>
                    <strong>Address</strong>
                  </h6>

                  {addresses && addresses.length > 0 ? (
                    addresses.map((address, index) => (
                      <div
                        key={index}
                        className={`border p-3 mt-2 rounded ${
                          selectedAddress === index
                            ? "border-success bg-light"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div
                          onClick={() => handleSelectAddress(index)}
                          style={{ flex: 1 }}
                        >
                          ✅ {address.type} {address.address}
                        </div>
                        {selectedAddress === index && (
                          <strong className="text-success">✔ Selected</strong>
                        )}

                        {/* Delete Icon */}
                        <span
                          style={{
                            color: "red",
                            cursor: "pointer",
                            marginLeft: "10px",
                          }}
                          onClick={() => removeAddress(address.id)}
                        >
                          🗑️
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No addresses added yet.</p>
                  )}

                  <button
                    className="btn mt-2"
                    style={{
                      width: "180px",
                      height: "40px",
                      backgroundColor: "rgb(115, 183, 52)",
                      color: "white",
                    }}
                    data-bs-toggle="modal"
                    data-bs-target="#addressModal"
                  >
                    + Add New Address
                  </button>
                </div>
              </div>

              <div className="card bg-light">
                <div className="card-body">
                  <h6>
                    <span className="badge bg-secondary me-2">2</span>Pickup
                    Slot
                  </h6>

                  {showPickupDates && (
                    <>
                      <p className="text-muted">
                        Choose preferred date for repair expert to visit you
                      </p>
                      <div className="d-flex gap-3 flex-wrap justify-content-around">
                        {generatePickupDates().map((item, index) => {
                          const itemDate = new Date(
                            item.year,
                            item.monthIndex - 1,
                            item.date
                          );

                          const today = new Date();
                          const tomorrow = new Date();
                          tomorrow.setDate(today.getDate() + 1);

                          const itemFormatted = formatDate(itemDate);
                          const todayFormatted = formatDate(today);
                          const tomorrowFormatted = formatDate(tomorrow);

                          let label = "";
                          if (itemFormatted === todayFormatted) {
                            label = "Today";
                          } else if (itemFormatted === tomorrowFormatted) {
                            label = "Tomorrow";
                          }

                          return (
                            <div
                              key={index}
                              className="p-3 text-center border rounded"
                              onClick={() => handleDateSelect(item.date)}
                              style={{
                                cursor: "pointer",
                                width: "100px",
                                height: "100px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor:
                                  selectedDate ===
                                  `${item.year}-${item.monthIndex
                                    .toString()
                                    .padStart(2, "0")}-${item.date
                                    .toString()
                                    .padStart(2, "0")}`
                                    ? "rgb(115, 183, 52)"
                                    : "",
                                color:
                                  selectedDate ===
                                  `${item.year}-${item.monthIndex
                                    .toString()
                                    .padStart(2, "0")}-${item.date
                                    .toString()
                                    .padStart(2, "0")}`
                                    ? "white"
                                    : "black",
                                border: "2px solid rgb(115, 183, 52)",
                                fontWeight: "bold",
                                borderRadius: "10px",
                                position: "relative",
                              }}
                            >
                              {label && (
                                <div
                                  style={{
                                    // position: "absolute",
                                    // top: "-20px",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    backgroundColor:
                                      selectedDate === item.date
                                        ? "rgb(115, 183, 52)"
                                        : "",
                                    color:
                                      selectedDate === item.date
                                        ? "white"
                                        : "black",
                                  }}
                                >
                                  {label}
                                </div>
                              )}
                              <strong>{item.date}</strong>
                              <div>{item.day}</div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Price Summary */}
            <div className="col-md-4">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h5 className="card-title border-bottom pb-3">
                    Price Summary
                  </h5>
                  <div class="d-flex flex-column text-capitalize fw-bold py-3">
                    {cart.length > 0 ? (
                      cart.map((result, idx) => (
                        <div
                          key={result.id}
                          class="d-flex justify-content-between w-100 mb-2"
                        >
                          <div class="d-flex align-items-center gap-2">
                            <span class="badge bg-light text-primary px-2">
                              {idx + 1}
                            </span>
                            <span>{result.product}</span>
                          </div>
                          <div class="d-flex gap-2">
                            <span class="text-dark fw-bold">
                              ₹{parseInt(result.payable_price)}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div>No Service Selected</div>
                    )}
                  </div>
                  <div class="d-flex justify-content-between align-items-center fw-bold mb-3">
                    <span>Total Amount</span>
                    <div class="d-flex align-items-center gap-2">
                      <span class="text-dark fs-5">₹{totalPrice}</span>
                    </div>
                  </div>

                  {selectedAddressData && (
                    <div className="border p-3 mt-2 rounded bg-light">
                      <h6>Selected Address:</h6>
                      <p>
                        {selectedAddressData.type},{" "}
                        {selectedAddressData.address}
                      </p>
                    </div>
                  )}
                  {selectedDate && (
                    <div className="border p-3 mt-2 rounded bg-light">
                      <h6>Selected Pickup Date:</h6>
                      <p>
                        {selectedDate}{" "}
                        {
                          generatePickupDates().find(
                            (d) => d.date === selectedDate
                          )?.month
                        }{" "}
                        {
                          generatePickupDates().find(
                            (d) => d.date === selectedDate
                          )?.year
                        }
                      </p>
                    </div>
                  )}

                  <button
                    className="book-now-btn w-100 d-flex justify-content-between align-items-center"
                    style={{
                      backgroundColor: "rgb(115, 183, 52)",
                      color: "white",
                    }}
                    disabled={isButtonDisabled}
                    onClick={handleContinue}
                  >
                    <span>
                      {isButtonDisabled ? "Processing..." : "Continue"}
                    </span>
                    <span>→</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <AddressModal
          setAddresses={setAddresses}
          setUpdateTrigger={setUpdateTrigger}
          addresses={addresses}
        />
      </div>

      <Footer />
    </>
  );
};

export default CheckOutPage;
